import {
  FieldContent,
  MessengerFormResponse,
  MessengerMessage,
} from 'lib-shared-legacy-types';

interface ParsedMessengerMessageContent {
  title: string;
  description: string;
}

export function isMessengerMessage(
  message: unknown,
): message is MessengerMessage {
  return (
    typeof message === 'object' &&
    message !== null &&
    'channel' in message &&
    message.channel === 'messenger'
  );
}

const stringifyFieldValue = (field: FieldContent): string => {
  if (typeof field.value === 'string') return field.value;

  if (field.label === 'Files') {
    const { files } = field.value.FileFieldResponseValue;
    return files
      .map(({ url, pretty_name }) => `[${pretty_name}](${url})`)
      .join(', ');
  }

  return '';
};

function parseMessengerFormContent({
  messenger_form_request,
}: MessengerMessage): ParsedMessengerMessageContent {
  try {
    const formResponse: MessengerFormResponse | null = JSON.parse(
      messenger_form_request,
    );

    const fieldsWithResponses =
      formResponse?.fields
        .flatMap<FieldContent>(Object.values)
        .filter((field) => field.value !== null)
        .map(stringifyFieldValue)
        .join('\n') ?? '';

    return {
      title: `[${formResponse?.labels.title ?? ''} form]`,
      description: fieldsWithResponses,
    };
  } catch (error) {
    return {
      title: 'Form',
      description: '',
    };
  }
}

function parseMessengerContentWithTitle({
  text,
  messenger_title,
}: MessengerMessage): ParsedMessengerMessageContent {
  if (messenger_title) {
    return { title: messenger_title, description: text ?? '' };
  }

  return { title: text ?? '', description: '' };
}

export function parseMessengerMessageContent(
  message: MessengerMessage,
): string {
  if (message.messenger_form_request) {
    const { title, description } = parseMessengerFormContent(message);
    return buildMessageContentString(title, description);
  }

  if (message.messenger_title) {
    const { title, description } = parseMessengerContentWithTitle(message);
    return buildMessageContentString(title, description);
  }

  return message.text ?? '';
}

function buildMessageContentString(
  title: string | null,
  description: string | null,
): string {
  if (title) {
    return `${title} ${description}`;
  }

  return description ?? '';
}
